<template>
	<div class="wrapper" :class="`route-${route_name}`" v-if="user">
		<LeftMenu @show-top-nav="showTopNav" ref="leftMenu" />
		<!-- Right Panel -->
		<div
			class="rightPanel mobile-experiment"
			:class="{ 'hide-bottom-nav': hideBottomNavigation, 'chat-overlay-open-desktop': isChatOverlayOpen }"
		>
			<div v-if="!hideTopNavigation" class="navigation-wrapper d-flex flex-column">
				<NavigationTop :show="!scrollingDown" ref="form" />
				<EventBanner @toggled="toggleEventBanner" />
			</div>
			<EventBanner v-else @toggled="toggleEventBanner" />

			<div id="main-container" ref="content" class="content d-flex flex-column">
				<div v-if="showTopBannerBox && !showProductEditorNavigation" class="d-flex flex-column pb-4">
					<AccountDisableHeader v-if="user.status === 0" />
					<WarningHeader v-if="!hasActiveSubscription">
						Your store isn't live yet. No worries! You can renew your subscription&nbsp;<a target="_self" href="/subscribe">here</a>.
					</WarningHeader>
					<WarningHeader v-if="showStripeSetupBanner">
						Heads up, customers can't purchase from you yet! Please
						<a target="_self" href="/account_settings/?tab=store">set up your Direct Deposit</a>
						to start selling
					</WarningHeader>
				</div>
				<RouterView />
			</div>
		</div>
		<!-- Pre Load animations -->
		<AppLottie name="store_loading" cacheAnimation />
		<AppLottie name="phone_width_sparkles" cacheAnimation />

		<ProductFeedbackModal />
		<PlayVideoModal />
		<ChangePlanModal />
		<StartTrialModal />
		<EnterStoreFullScreenModal ref="enterStoreFullScreen" skipFadeIn />
		<AppModal
			title="You haven't saved!"
			subheading="Any changes you may have made to this product will be lost!"
			action="Continue Editing"
			@cta="cancelLeave"
			secondary-action="Leave"
			@secondary-cta="confirmLeave"
			ref="blockLeaveModal"
		>
		</AppModal>
		<PortalTarget name="main" multiple />
		<MFAConfigurationModal :flowStageStart="this.$constants.MFA.FLOW_STAGES.RESTORE_CONFIGURATION" />
		<ChatOverlay />

		<!-- Floating Chat Button -->
		<div v-if="showFloatingChatButton" class="floating-chat-button" :class="{ 'hide-bottom-nav': hideBottomNavigation }" @click="openChatOverlay">
			<InlineSvg src="/images/dynamic_onboarding_v2/stanley-icon-white.svg" />
		</div>
	</div>
	<div v-else class="h-100 w-100">
		<HollowDotsSpinner :animation-duration="1000" :dot-size="18" class="h-100 d-flex align-items-center mx-auto" :dots-num="3" color="#3a36db" />
	</div>
</template>
<script>
	import { mapGetters, mapActions } from 'vuex'
	import Cookies from 'js-cookie'
	import userflow from '~/plugins/userflow.js'
	import NavigationTop from '~/components/navigation/NavigationTop'
	import LeftMenu from '~/components/navigation/LeftMenu'
	import ProductFeedbackModal from '~/components/modals/ProductFeedbackModal'
	import AccountDisableHeader from '~/components/headers/AccountDisableHeader'
	import PlayVideoModal from '~/components/PlayVideoModal'
	import ChangePlanModal from '~/pages/auth/ChangePlanModal/ChangePlanModal'
	import StartTrialModal from '~/pages/auth/StartTrialModal'
	import EnterStoreFullScreenModal from '~/components/modals/EnterStoreFullScreenModal'
	import MFAConfigurationModal from '~/pages/auth/MFAConfigurationModal'
	import ChatOverlay from '~/components/chat/ChatOverlay'
	import OnboardingMixin from '~/pages/dashboard/OnboardingMixin.js'
	import EventBanner from '~/components/navigation/EventBanner'
	import IntegrationErrorsMixin from '~/pages/settings/IntegrationErrorsMixin.js'

	export default {
		middleware: ['auth', 'subscription-check', 'experiment-setup'],
		components: {
			NavigationTop,
			LeftMenu,
			ProductFeedbackModal,
			AccountDisableHeader,
			PlayVideoModal,
			ChangePlanModal,
			StartTrialModal,
			EnterStoreFullScreenModal,
			MFAConfigurationModal,
			ChatOverlay,
			EventBanner,
		},
		mixins: [OnboardingMixin, IntegrationErrorsMixin],
		props: {
			fromFullScreen: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				pendingNext: null,
				isActive: 'Dashboard',
				route_name: '',
				settings: {
					maxScrollbarLength: 60,
				},
				deviceType: '',
				isInitialStatusLoaded: false,
				previousScrollY: 0,
				getUserTimer: null,
				scrollingDown: false,
				scrollPosition: 0,
				scrollDirection: null,
				scrollDirectionStartTime: null,
				directionInterval: null,
				showingBanner: undefined,
			}
		},
		mounted() {
			// Just in-case we still have gary theme from previous pages
			document.body.classList.remove(`theme-gary`)
			this.$root.$on('confirm-leave', this.handleConfirmLeave)
			window.addEventListener('scroll', this.handleScroll, { passive: true })
			this.$refs.content?.addEventListener('scroll', this.handleScroll, { passive: true })

			// We want to show the loading animation to help give context when coming into the store from a full screen context
			this.checkAnimateFullScreenEnter()
			// Fast Loading of Component Data
			this.fetchTransactions({ page: 1 }) // Important to match what's in Orders.vue to achieve cached loading
			this.fetchCustomers({ page: 1 })
			this.fetchBalance()
			this.fetchRevenueChart()
			this.fetchEmailTemplates()
			this.fetchResellProducts()

			this.fetchStripeConnected().then(() => {
				this.isInitialStatusLoaded = true
			})

			if (Cookies.get('language_code')) {
				this.$stanAnalytics('admin_user_language_code', {
					meta: { user_id: this.user.user_id, username: this.user.username },
					props: { language_code: Cookies.get('language_code') },
				})
			}
		},
		created() {
			this.route_name = this.$router.currentRoute.name
			this.isActive = this.route_name
			if (this.user && !Cookies.get('logon_event')) {
				this.$stanAnalytics('logon', { meta: { user_id: this.user.user_id, username: this.user.username } })
			}

			if (this.isSuperAdmin && this.$route.name !== 'configure-mfa' && this.$configSettings.env_name === 'production') {
				this.$axios
					.get('v1/mfa/list', {
						baseURL: this.$configSettings.authApiUrl,
					})
					.then(response => {
						// no enabled mFA config
						if (response.data.filter(x => x.status === 1).length === 0) {
							this.$router.push({ name: 'configure-mfa' })
						}
					})
					.catch()
			}

			// ^^ Auto Extend the Cookie time to 15 min
			const in15Minutes = 1 / 24 / 4 // js-cookies uses days as base unit
			Cookies.set('logon_event', '1', { expires: in15Minutes })
			if (userflow && !this.isAdminLoggedInAsUser && !this.isSuperAdmin) {
				// Userflow Initialization
				userflow.InitializeUserflow()
				userflow.UpdateUserflowIdentityData(this.user)

				this.userflow = userflow
				this.checkUpdateUserFlowData()
			}

			if (window.clarity && !Cookies.get('clarity_experiment_set')) {
				Cookies.set('clarity_experiment_set', '1', { expires: in15Minutes })

				// Add the new tag in clarity for day count from user.created_at to now
				const diffTime = Math.abs(new Date() - new Date(this.user.created_at * 1000))
				const msInDay = 1000 * 60 * 60 * 24
				if (window.clarity) {
					window.clarity('set', 'days_from_signup', `${Math.ceil(diffTime / msInDay)}`)
				}

				for (const experiment in this.user.experiments) {
					if (window.clarity) {
						window.clarity('set', `experiment-${experiment}`, `${this.user.experiments[experiment]}`)
					}
				}
			}

			this.initalizeChallenge()
			this.fetchStore()
			this.fetchFunnels()
			this.fetchFlows()
			this.fetchCommunities({ configSettings: this.$configSettings })
			this.$store.dispatch('Auth/getPlatformSubscription')
		},
		computed: {
			...mapGetters('Global', [
				'showNavigation',
				'showPreview',
				'showProductEditorPreview',
				'showProductEditorNavigation',
				'hideTopNavigation',
				'hideBottomNavigation',
			]),
			...mapGetters('Auth', ['user', 'isAdminLoggedInAsUser', 'isSuperAdmin', 'hasActiveSubscription']),
			...mapGetters('Stores', ['getStore', 'getStoreAllPages', 'getStorePublishedPages']),
			...mapGetters('SiteSettingsStore', ['isStripeConnected', 'isPaypalConnected']),
			...mapGetters('Chat', ['isChatOverlayOpen']),
			hasDynamicDashboard() {
				return this.user.has_dynamic_dashboard
			},
			hasPaidProductsNoDirectDeposit() {
				let hasPaidProduct = false
				for (const page of this.getStorePublishedPages || []) {
					if (this.$commonHelper.isPaidProduct(page.data.product)) {
						hasPaidProduct = true
						break
					}
				}
				return hasPaidProduct && this.user.payment_restricted.linksite
			},
			showTopBannerBox() {
				return this.user.status === 0 || !this.hasActiveSubscription || this.showStripeSetupBanner
			},
			showStripeSetupBanner() {
				// Dashboard and income pages have there own banner
				if (this.hasStripeAccountIssues && (this.route_name === 'dashboard' || this.route_name === 'income')) {
					return false
				}
				return (
					this.hasActiveSubscription &&
					this.hasPaidProductsNoDirectDeposit &&
					// if user has onboarding, only show banner if past 2nd step + not on dashboard page (dashboard page already shows setup direct deposit)
					(!this.hasOnboardingDashboard ||
						(this.user['onboarding-dashboard']?.profile_image_uploaded &&
							this.user['onboarding-dashboard']?.social_links_added &&
							this.route_name !== 'dashboard'))
				)
			},

			hasOnboardingDashboard() {
				return this.user['onboarding-dashboard']?.status === 'active'
			},
			pageType() {
				return typeof this.$route.params.slug !== 'undefined' ? this.$route.params.slug : 'default'
			},

			forceReferralPreOnboarding() {
				const referralPreOnboarding = this.$route.query.referralPreOnboarding
				const template = Object.values(this.$constants.REFERRAL_DASHBOARD_TEMPLATE_NAMES).includes(referralPreOnboarding)
				return template ? referralPreOnboarding : null
			},
			getReferralTemplate() {
				if (this.forceReferralPreOnboarding) {
					return this.forceReferralPreOnboarding
				}
				return this.user?.use_referral_template_dashboard
			},
			showFloatingChatButton() {
				// Do not mess with a custom onboarding flow
				if (this.showReferralPreOnboardingDashboard) {
					return false
				}

				return this.hasDynamicDashboard && !this.isChatOverlayOpen && !this.showNavigation
			},
		},
		watch: {
			showNavigation(newVal) {
				const self = this
				if (newVal) {
					this.previousScrollY = window.scrollY
					$('body').addClass('menu-active')
				} else {
					$('body').removeClass('menu-active')
					setTimeout(() => self.$refs.content.scrollTo({ top: this.previousScrollY, behavior: 'smooth' }), 10)
				}
			},
			showPreview(newVal) {
				const self = this
				if (newVal) {
					this.previousScrollY = window.scrollY
					$('body').addClass('preview-active')
				} else {
					$('body').removeClass('preview-active')
					setTimeout(() => self.$refs.content.scrollTo({ top: this.previousScrollY, behavior: 'smooth' }), 10)
				}
			},
			$route(newVal) {
				const self = this
				this.route_name = newVal.name
				if (newVal.params.scroll !== false) {
					setTimeout(() => self.$refs.content.scrollTo({ top: 0 }), 100)
				}
			},
			user(newUser) {
				if (newUser && window.Beacon) {
					window.Beacon('identify', {
						name: newUser.full_name,
						email: newUser.email,
						avatar: newUser.profile_image,
					})
				}
			},
			'user.profile_image': {
				handler() {
					if (this.userflow) {
						userflow.UpdateUserflowIdentityData(this.user)
					}
				},
			},
			isStripeConnected(newVal) {
				if (this.userflow && (newVal === true || newVal === false)) {
					this.checkUpdateUserFlowData()
				}
			},
			isPaypalConnected(newVal) {
				if (this.userflow && (newVal === true || newVal === false)) {
					this.checkUpdateUserFlowData()
				}
			},
			getStoreAllPages(newVal) {
				if (this.userflow && newVal) {
					this.checkUpdateUserFlowData()
				}
			},
			fromFullScreen: {
				handler(newVal) {
					// This is to trigger the entering store transition animation when coming from an existing full screen page that already has the main container loaded
					if (newVal) {
						this.checkAnimateFullScreenEnter()
					}
				},
				immediate: true,
			},
			isAdminLoggedInAsUser: {
				immediate: true,
				handler() {
					this.updateNavbarOffset()
				},
			},
		},
		methods: {
			...mapActions('Stores', ['fetchStore', 'fetchFunnels', 'fetchEmailTemplates']),
			...mapActions('SiteSettingsStore', ['fetchStripeConnected', 'fetchStanPayment']),
			...mapActions('Flows', ['fetchFlows']),
			...mapActions('Transactions', ['fetchTransactions', 'fetchBalance', 'fetchRevenueChart']),
			...mapActions('ResellRights', ['fetchResellProducts']),
			...mapActions('Audience', ['fetchCustomers']),
			...mapActions('Communities', ['fetchCommunities']),
			...mapActions('Challenge', ['initalizeChallenge']),
			...mapActions('Chat', ['openChatOverlay']),
			showTopNav() {
				this.scrollingDown = false
			},
			handleConfirmLeave(next) {
				$(this.$refs.blockLeaveModal.$el).modal('show')
				this.pendingNext = next
			},
			confirmLeave() {
				if (this.pendingNext) {
					this.pendingNext()
					this.pendingNext = null
				}
				$(this.$refs.blockLeaveModal.$el).modal('hide')
			},
			cancelLeave() {
				if (this.pendingNext) {
					this.pendingNext(false)
					this.pendingNext = null
				}
				$(this.$refs.blockLeaveModal.$el).modal('hide')
			},
			handleScroll() {
				const container = this.$refs.content
				if (!container) return

				const maxScroll = container.scrollHeight - container.clientHeight
				const current = Math.min(container.scrollTop, maxScroll)

				if (maxScroll <= 50) {
					this.scrollingDown = false
					return
				}
				const direction = current > this.scrollPosition ? 'down' : 'up'
				const now = Date.now()
				// Force show at top
				if (current <= 10) {
					this.scrollingDown = false
					this.scrollPosition = current // Keep scrollPosition in sync
					return
				}
				// Force hide at bottom
				if (maxScroll - current <= 10) {
					this.scrollingDown = true
					this.scrollPosition = current
					return
				}
				if (direction !== this.scrollDirection) {
					this.scrollDirection = direction
					this.scrollDirectionStartTime = now
				} else {
					if (direction === 'down' && !this.scrollingDown && this.scrollDirectionStartTime && now - this.scrollDirectionStartTime >= 100) {
						this.scrollingDown = true
						this.scrollDirectionStartTime = null
					}

					if (direction === 'up' && this.scrollingDown && this.scrollDirectionStartTime && now - this.scrollDirectionStartTime >= 100) {
						this.scrollingDown = false
						this.scrollDirectionStartTime = null
					}
				}

				this.scrollPosition = current
			},
			activeTab(tabName, query, params) {
				this.$router.push({ name: tabName, query, params }).catch(() => {})
			},
			checkUpdateUserFlowData() {
				if (!this.userflow) {
					return
				}

				// Assign Sections experiment to userflow
				const obj = {}
				for (const experiment in this.user.experiments) {
					obj[experiment] = this.user.experiments[experiment]
				}

				if (this.user.metadata) {
					// Assign Metadata to userflow
					for (const m in this.user.metadata) {
						obj[m] = this.user.metadata[m]
					}
				}

				if (this.isStripeConnected === true || this.isStripeConnected === false) {
					obj.is_stripe_connected = this.isStripeConnected
				}

				if (this.isPaypalConnected === true || this.isPaypalConnected === false) {
					obj.is_paypal_connected = this.isPaypalConnected
				}

				if (this.getStoreAllPages) {
					// Only pages that are published
					const allPublishedProducts = this.getStoreAllPages.filter(f => f.status === 2)
					obj.all_published_products_count = allPublishedProducts.length
					obj.all_published_paid_products_count = allPublishedProducts.filter(f => this.$commonHelper.isPaidProduct(f.data.product)).length
				}

				if (obj) {
					this.userflow.UserflowCustomBulkUpdate(obj)
				}
			},
			checkAnimateFullScreenEnter() {
				if (this.fromFullScreen) {
					this.$refs.enterStoreFullScreen.show()
					setTimeout(() => {
						this.$refs.enterStoreFullScreen.hide()
					}, 2500)
				}
			},
			updateNavbarOffset() {
				if (this.hideTopNavigation) {
					document.documentElement.style.setProperty('--navbar-dynamic-offset', 0)
				} else {
					const baseOffset = 50

					// Account for event banner
					const showingBanner = this.showingBanner !== undefined
					const bannerOffset = showingBanner && this.showingBanner ? 50 : 0

					// Account for `is logged in as` text on top of navbar
					const adminLoggedInAsUserOffset = this.isAdminLoggedInAsUser ? 25 : 0

					const totalOffset = baseOffset + bannerOffset + adminLoggedInAsUserOffset

					document.documentElement.style.setProperty('--navbar-dynamic-offset', `${totalOffset}px`)
				}
			},
			toggleEventBanner(showing) {
				this.showingBanner = showing
				this.updateNavbarOffset()
			},
		},
		destroyed() {
			this.$root.$off('confirm-leave', this.handleConfirmLeave)
			this.$refs.content?.removeEventListener('scroll', this.handleScroll)
			window.removeEventListener('scroll', this.handleScroll)
			clearInterval(this.directionInterval)
			if (this.getUserTimer) {
				clearTimeout(this.getUserTimer)
			}
		},
	}
</script>
<style lang="scss" scoped>
	.wrapper {
		display: flex;
		transition: all ease 0.3s;
		height: 100%;
		overflow: hidden;
	}

	.navigation-wrapper {
		position: absolute;
		width: calc(100% - 220px);
		z-index: 100;
		background: #fff;

		@media (max-width: 992px) {
			width: 100%;
		}
	}

	.rightPanel {
		width: 100%;
		transition: all ease 0.3s;
		display: flex;
		flex-direction: column;
		padding-bottom: 74px;

		&.chat-overlay-open-desktop {
			padding-right: 420px;
		}

		&.mobile-experiment {
			padding-bottom: 54px;

			@media (min-width: 992px) {
				padding-bottom: 0px;
			}
		}

		&.hide-bottom-nav {
			padding-bottom: 0px;
		}

		height: 100vh;
		/* If dvh is supported, apply it */
		@supports (height: 100dvh) {
			height: 100dvh;
		}

		@media (min-width: 992px) {
			padding-bottom: 0px;
			padding-left: 210px;
		}

		.content {
			transition: padding 0.3s ease;
			--padding-right-panel-top: calc(24px + var(--navbar-dynamic-offset));
			flex-basis: 0;
			flex-grow: 1;
			overflow-x: hidden;
			overflow-y: scroll;
			padding: 24px;
			padding-top: var(--padding-right-panel-top);
			position: relative;
			@media (min-width: 992px) {
				--padding-right-panel-top: calc(30px + var(--navbar-dynamic-offset));
				padding: var(--padding-right-panel-top) 30px 40px;
			}

			@media (max-width: 768px) {
				padding-inline: 16px;
				-ms-overflow-style: none;
				scrollbar-width: none;
				&::-webkit-scrollbar {
					display: none;
				}
				&.remove-padding-top {
					padding-top: 0;
				}
			}
		}
	}
	.preview-active .rightPanel {
		display: none;
		@media (min-width: 992px) {
			display: block;
		}
	}
	.menu-active .rightPanel {
		display: none;
		@media (min-width: 992px) {
			display: block;
		}
	}

	@media (max-width: 767px) {
		.flex-xs-column-reverse {
			flex-direction: column-reverse !important;
		}
		.xs-d-flex {
			display: flex !important;
		}
		.payment-alert {
			margin-bottom: 10px;
		}
	}
	.logo {
		margin: auto;
		padding: 15px;
		@media (min-width: 768px) {
			padding-top: 0;
		}
		@media (min-width: 992px) {
			display: none;
		}
	}

	.floating-chat-button {
		position: fixed;
		bottom: 16px;
		right: 16px;
		width: 46px;
		height: 46px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		z-index: 1000;
		transition: all 0.3s ease;
		background: linear-gradient(180deg, #b5bcff 0%, #5791ff 100%);

		&:hover {
			transform: scale(1.1);
		}

		@media (max-width: 991px) {
			bottom: 76px;

			&.hide-bottom-nav {
				bottom: 16px;
			}
		}
	}
</style>
<style lang="scss">
	.BeaconContainer {
		@media (max-width: 991px) {
			bottom: 90px !important;
		}
	}
	.BeaconFabButtonFrame {
		@media (max-width: 991px) {
			bottom: 90px !important;
		}
	}
</style>
